/* ─────────────────────────────  ReportPrintPage.jsx  ─────────────────────────────
   Vista de impresión membretada de un reporte personalizado.
   - Sin sidebar, header de app, ni chat flotante (vive afuera del layout).
   - Header con logo + nombre de organización; footer con fecha + paginación.
   - Renderiza los blocks del reporte vía Tiptap en modo readOnly.
   - Auto-dispara window.print() al terminar de cargar (incluyendo imágenes
     base64 de las gráficas), para que el usuario pueda "Guardar como PDF"
     con un solo clic adicional.
   ──────────────────────────────────────────────────────────────────────── */

import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import { useOrganization } from '../../contexts/OrganizationContext';
import logoDefault from '../../assets/img/logo-data-new.png';
import './ReportPrintPage.css';

const STRANDS_API_URL = process.env.REACT_APP_STRANDS_API_URL || 'http://localhost:5001';

function emptyDoc() {
  return { type: 'doc', content: [{ type: 'paragraph' }] };
}

function normalizeContent(blocks) {
  // Compatible con varios shapes que han pasado por la app
  if (!blocks) return emptyDoc();
  if (typeof blocks === 'string') {
    try { return normalizeContent(JSON.parse(blocks)); } catch { return emptyDoc(); }
  }
  if (Array.isArray(blocks)) return { type: 'doc', content: blocks };
  if (blocks?.type === 'doc') return blocks;
  return emptyDoc();
}

function formatDate(d) {
  try {
    return new Date(d).toLocaleDateString('es-MX', {
      day: '2-digit', month: 'long', year: 'numeric'
    });
  } catch {
    return '';
  }
}

const ReportPrintPage = () => {
  const { id_report } = useParams();
  const { user } = useAuthenticator((context) => [context.user]);
  const { logoUrl, displayName } = useOrganization();

  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const printedRef = useRef(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Image.configure({ inline: false, allowBase64: true }),
      Table.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    editable: false,
    content: emptyDoc(),
  });

  // Cargar el reporte
  useEffect(() => {
    if (!id_report || !user?.username || !editor) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${STRANDS_API_URL}/api/reports/${id_report}?username=${encodeURIComponent(user.username)}`
        );
        if (!res.ok) throw new Error('No se pudo cargar el reporte');
        const data = await res.json();
        if (cancelled || !data.success) return;
        setReport(data.report);
        editor.commands.setContent(normalizeContent(data.report.blocks), false);
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    })();
    return () => { cancelled = true; };
  }, [id_report, user, editor]);

  // Cuando todo está cargado (reporte + imágenes), dispara la ventana de
  // imprimir. Lo hacemos UNA sola vez (printedRef) para que si el usuario
  // cancela y queda viendo la página, no se vuelva a disparar.
  useEffect(() => {
    if (printedRef.current) return;
    if (!report || !editor) return;

    const imgs = Array.from(document.images);
    const pending = imgs.filter(im => !im.complete);

    const fire = () => {
      printedRef.current = true;
      // Pequeño delay para que Chrome termine de paginar.
      setTimeout(() => window.print(), 250);
    };

    if (pending.length === 0) {
      fire();
      return;
    }

    let left = pending.length;
    const dec = () => { if (--left === 0) fire(); };
    pending.forEach(im => {
      im.addEventListener('load', dec, { once: true });
      im.addEventListener('error', dec, { once: true });
    });
  }, [report, editor]);

  if (error) {
    return <div className="report-print-error">Error: {error}</div>;
  }
  if (!report || !editor) {
    return <div className="report-print-loading">Cargando reporte…</div>;
  }

  const orgName = displayName || 'Data Bunker';
  const orgLogo = logoUrl || logoDefault;

  return (
    <div className="report-print-page">
      {/* Membrete superior — se repite en cada hoja vía CSS @page si el
          navegador lo soporta; si no, sale solo en la primera. */}
      <header className="report-print-header">
        <div className="report-print-header-left">
          <img
            src={orgLogo}
            alt={orgName}
            className="report-print-logo"
            onError={(e) => { e.target.src = logoDefault; }}
          />
          <div className="report-print-org">
            <div className="report-print-org-name">{orgName}</div>
            <div className="report-print-org-sub">Reporte de análisis</div>
          </div>
        </div>
        <div className="report-print-header-right">
          <div className="report-print-date-label">Generado</div>
          <div className="report-print-date">{formatDate(new Date())}</div>
        </div>
      </header>

      <h1 className="report-print-title">{report.title || 'Reporte sin título'}</h1>
      <div className="report-print-meta">
        Preparado por <strong>{user?.username || '—'}</strong>
      </div>

      <div className="report-print-body">
        <EditorContent editor={editor} />
      </div>

      <footer className="report-print-footer">
        <span>{orgName} · Documento confidencial</span>
        <span className="report-print-footer-page">Generado automáticamente</span>
      </footer>

      {/* Botón visible solo en pantalla — desaparece al imprimir */}
      <div className="report-print-actions no-print">
        <button onClick={() => window.print()} className="report-print-btn">
          🖨️ Imprimir / Guardar como PDF
        </button>
        <button onClick={() => window.close()} className="report-print-btn-secondary">
          Cerrar
        </button>
      </div>
    </div>
  );
};

export default ReportPrintPage;
